// AppointmentForm — the field stack + footer used by the request-a-viewing
// modal (create) and the edit flow on the appointment detail sheet. In create
// mode it opens a verification request (PENDING_SOURCE, via useRequestViewing);
// edit mode patches a confirmed appointment's info.
//
// Visual hierarchy follows the design comp at
// design/src/components/desktop/Appointments.jsx (lines 88–171). The form
// renders ONLY the field stack + footer — the calling Dialog/Sheet
// supplies its own header (so we don't double-up on chrome when the form
// renders inline on the lead detail sheet, or inside the appointment
// detail sheet's edit mode).
//
// Field set:
//   - Property picker (Popover + Command)        [showLeadSelector only]
//   - Lead picker     (Popover + Command)        [showLeadSelector only]
//   - Broker select   (plain shadcn Select)
//   - Date (native <input type="date">) + Time (native <input type="time">)
//   - Time quick-pick chips that prefill the time input
//   - Notes textarea with a decorative "AI gợi ý" hint label
//   - Footer: Hủy + Yêu cầu xem nhà / Lưu
//
// What's intentionally NOT here vs the design:
//   - The reminder badges (15 phút trước / SMS / Zalo) — out of scope.
//   - The Zalo confirm toggle in the footer — also out of scope.

import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Calendar, Clock, Loader2, Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiError } from "@/lib/api-client";
import { localizeError } from "@/lib/errorMessages";
import { applyServerValidationErrors } from "@/lib/form-errors";
import {
  useRequestViewing,
  useUpdateAppointment,
} from "@/features/appointment/api";
import { useBrokers } from "@/features/appointment/hooks/use-brokers";
import { useLeads } from "@/features/lead/api";
import { useProperties } from "@/features/property/api";
import { useAuthStore } from "@/lib/auth-store";
import { LeadPickerField } from "@/features/appointment/components/lead-picker-field";
import { PropertyPickerField } from "@/features/appointment/components/property-picker-field";
import type {
  Appointment,
  RequestViewingInput,
} from "@/features/appointment/types";

// ---------------------------------------------------------------------------
// Form schema — date + time are kept as separate fields and recombined on
// submit. Native `<input type="date">` emits `YYYY-MM-DD`; `<input type=
// "time">` emits `HH:mm`. We re-assemble into a local ISO string in the
// submit handler.
// ---------------------------------------------------------------------------

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}$/;

const appointmentFormSchema = z.object({
  dateOnly: z
    .string()
    .min(1, "Vui lòng chọn ngày")
    .regex(DATE_RE, "Ngày không hợp lệ"),
  timeOnly: z
    .string()
    .min(1, "Vui lòng chọn giờ")
    .regex(TIME_RE, "Giờ không hợp lệ"),
  leadId: z.string().min(1, "Vui lòng chọn khách hàng tiềm năng"),
  propertyId: z.string().optional().or(z.literal("")),
  brokerId: z.string().min(1, "Vui lòng chọn đại diện"),
  notes: z.string().max(2000, "Tối đa 2000 ký tự").optional().or(z.literal("")),
});

type AppointmentFormValues = z.input<typeof appointmentFormSchema>;

/** Quick-pick chips below the time input; tap to prefill the time field. */
const TIME_QUICK_PICKS = ["09:00", "10:30", "11:00", "14:00", "15:00", "16:30"] as const;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Pre-fill values for the create flow. All fields are optional. */
interface AppointmentFormPrefill {
  leadId?: number | undefined;
  propertyId?: number | null | undefined;
  /** Display label for the locked property in the lead-context flow. */
  propertyName?: string | undefined;
  brokerId?: number | undefined;
  scheduledAt?: string | undefined;
}

interface AppointmentFormProps {
  mode: "create" | "edit";
  appointment?: Appointment | undefined;
  prefill?: AppointmentFormPrefill | undefined;
  /**
   * Lead-originated flow (lead detail, kanban): the lead is fixed (hidden — the
   * surface header already names the customer) and its property is locked
   * read-only when set, since a lead pursues one property. Falls back to a
   * property picker only when the lead has none yet.
   */
  leadContext?: boolean | undefined;
  onSuccess: (appointment: Appointment) => void;
  onCancel: () => void;
  submitLabel?: string | undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AppointmentForm({
  mode,
  appointment,
  prefill,
  leadContext = false,
  onSuccess,
  onCancel,
  submitLabel,
}: AppointmentFormProps) {
  const requestMutation = useRequestViewing();
  const updateMutation = useUpdateAppointment(appointment?.id ?? 0);
  const brokersQuery = useBrokers();
  // Leads are not paginated; load the full list for the combobox.
  const leadsQuery = useLeads({});
  // Properties pagination caps at 100 per page; surface size 50 (the
  // common case is "pick from recent listings"; agents can search by
  // typing into the combobox).
  const propertiesQuery = useProperties({ page: 0, size: 50 });
  const currentUser = useAuthStore((s) => s.user);

  const brokers = brokersQuery.data ?? [];
  const leads = leadsQuery.data ?? [];
  const properties = propertiesQuery.data?.items ?? [];

  const initialBrokerId = useMemo(() => {
    if (appointment) return String(appointment.brokerId);
    if (prefill?.brokerId !== undefined) return String(prefill.brokerId);
    if (currentUser?.id) return String(currentUser.id);
    return "";
  }, [appointment, prefill?.brokerId, currentUser?.id]);

  const initialLeadId = useMemo(() => {
    if (appointment?.leadId) return String(appointment.leadId);
    if (prefill?.leadId !== undefined) return String(prefill.leadId);
    return "";
  }, [appointment?.leadId, prefill?.leadId]);

  const initialPropertyId = useMemo(() => {
    if (appointment?.propertyId) return String(appointment.propertyId);
    if (prefill?.propertyId !== undefined && prefill.propertyId !== null) {
      return String(prefill.propertyId);
    }
    return "";
  }, [appointment?.propertyId, prefill?.propertyId]);

  const initialDateTime = useMemo(() => {
    const raw =
      appointment?.scheduledAt ?? prefill?.scheduledAt ?? defaultScheduledAt();
    return splitDateTime(raw);
  }, [appointment?.scheduledAt, prefill?.scheduledAt]);

  const form = useForm<AppointmentFormValues>({
    resolver: zodResolver(appointmentFormSchema),
    defaultValues: {
      dateOnly: initialDateTime.dateOnly,
      timeOnly: initialDateTime.timeOnly,
      leadId: initialLeadId,
      propertyId: initialPropertyId,
      brokerId: initialBrokerId,
      notes: appointment?.notes ?? "",
    },
  });
  const {
    handleSubmit,
    setValue,
    watch,
    register,
    formState: { errors, isSubmitting },
  } = form;

  const timeValue = watch("timeOnly");
  const brokerIdValue = watch("brokerId");
  const leadIdValue = watch("leadId");
  const propertyIdValue = watch("propertyId");
  // Lead flow: the property is fixed to the lead's interest — show it read-only
  // when present, fall back to the picker only when the lead has none yet.
  const propertyLocked =
    leadContext && propertyIdValue !== undefined && propertyIdValue !== "";

  const onSubmit = async (raw: AppointmentFormValues) => {
    const values = appointmentFormSchema.parse(raw);

    const scheduledAtIso = combineDateTime(values.dateOnly, values.timeOnly);
    if (scheduledAtIso === null) {
      form.setError("dateOnly", {
        type: "manual",
        message: "Ngày giờ không hợp lệ",
      });
      return;
    }

    const trimmedNotes =
      typeof values.notes === "string" && values.notes.trim().length > 0
        ? values.notes.trim()
        : undefined;

    const propertyIdNumber =
      values.propertyId !== undefined && values.propertyId !== ""
        ? Number.parseInt(values.propertyId, 10)
        : undefined;

    // A viewing request needs a property — that's where the Source (who
    // confirms with the owner) is resolved from.
    if (mode === "create" && propertyIdNumber === undefined) {
      form.setError("propertyId", {
        type: "manual",
        message: "Vui lòng chọn bất động sản",
      });
      return;
    }

    try {
      if (mode === "create") {
        const body: RequestViewingInput = {
          leadId: Number.parseInt(values.leadId, 10),
          propertyId: propertyIdNumber!,
          proposedAt: scheduledAtIso,
          brokerId: Number.parseInt(values.brokerId, 10),
          ...(trimmedNotes !== undefined ? { note: trimmedNotes } : {}),
        };
        const created = await requestMutation.mutateAsync(body);
        toast.success("Đã gửi yêu cầu xem nhà — chờ nguồn xác nhận");
        onSuccess(created);
      } else {
        if (!appointment) throw new Error("Edit mode requires an appointment");
        const updated = await updateMutation.mutateAsync({
          leadId: Number.parseInt(values.leadId, 10),
          brokerId: Number.parseInt(values.brokerId, 10),
          scheduledAt: scheduledAtIso,
          ...(propertyIdNumber !== undefined
            ? { propertyId: propertyIdNumber }
            : {}),
          notes: trimmedNotes ?? "",
        });
        toast.success("Đã cập nhật");
        onSuccess(updated);
      }
    } catch (error) {
      if (error instanceof ApiError) {
        const handled = applyServerValidationErrors(form, error);
        if (handled) return;
        toast.error(localizeError(error));
        return;
      }
      toast.error("Không thể kết nối đến máy chủ");
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex flex-col gap-3"
      aria-label={mode === "create" ? "Yêu cầu xem nhà" : "Sửa lịch hẹn"}
    >
      {/* Property — locked read-only to the lead's interest in the lead flow;
          a picker otherwise (incl. a lead that has no property yet). */}
      {propertyLocked ? (
        <FieldRow label="Bất động sản" htmlFor={undefined}>
          <div className="flex h-9 items-center rounded-[9px] border border-border bg-muted/40 px-2.5 text-[14px] text-ink-soft">
            {prefill?.propertyName ?? "Bất động sản đã chọn"}
          </div>
          <input type="hidden" {...register("propertyId")} />
        </FieldRow>
      ) : (
        <FieldRow
          label="Bất động sản"
          htmlFor={undefined}
          error={errors.propertyId?.message}
        >
          <PropertyPickerField
            value={
              propertyIdValue !== undefined && propertyIdValue !== ""
                ? Number.parseInt(propertyIdValue, 10)
                : null
            }
            properties={properties}
            isPending={propertiesQuery.isPending}
            onChange={(id) => {
              setValue("propertyId", id !== null ? String(id) : "", {
                shouldDirty: true,
              });
              if (id !== null) form.clearErrors("propertyId");
            }}
          />
        </FieldRow>
      )}

      {/* Lead — a picker in the general flow; fixed (hidden) in the lead flow,
          where the surface header already names the customer. */}
      {leadContext ? (
        <input type="hidden" {...register("leadId")} />
      ) : (
        <FieldRow label="Khách hàng" htmlFor={undefined} error={errors.leadId?.message}>
          <LeadPickerField
            value={
              leadIdValue !== undefined && leadIdValue !== ""
                ? Number.parseInt(leadIdValue, 10)
                : null
            }
            leads={leads}
            isPending={leadsQuery.isPending}
            onChange={(id) =>
              setValue("leadId", String(id), {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
          />
        </FieldRow>
      )}

      <FieldRow label="Đại diện" htmlFor="brokerId" error={errors.brokerId?.message}>
        <Select
          value={brokerIdValue}
          onValueChange={(v) =>
            setValue("brokerId", v, { shouldValidate: true, shouldDirty: true })
          }
          disabled={brokersQuery.isPending}
        >
          <SelectTrigger id="brokerId" className="w-full">
            <SelectValue
              placeholder={brokersQuery.isPending ? "Đang tải..." : "Chọn đại diện"}
            />
          </SelectTrigger>
          <SelectContent>
            {brokers.map((broker) => (
              <SelectItem key={broker.id} value={String(broker.id)}>
                {broker.fullName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>

      <div className="grid grid-cols-[1.3fr_1fr] gap-2.5">
        <FieldRow label="Ngày" htmlFor="dateOnly" error={errors.dateOnly?.message}>
          <IconInput icon={<Calendar aria-hidden="true" className="size-3.5" />}>
            <input
              id="dateOnly"
              type="date"
              {...register("dateOnly")}
              aria-invalid={errors.dateOnly ? "true" : "false"}
              className="h-full w-full bg-transparent text-[14px] outline-none placeholder:text-ink-faint"
            />
          </IconInput>
        </FieldRow>

        <FieldRow label="Giờ" htmlFor="timeOnly" error={errors.timeOnly?.message}>
          <div className="flex gap-1.5">
            <IconInput
              icon={<Clock aria-hidden="true" className="size-3.5" />}
              className="flex-1"
            >
              <input
                id="timeOnly"
                type="time"
                step={300}
                {...register("timeOnly")}
                aria-invalid={errors.timeOnly ? "true" : "false"}
                className="h-full w-full bg-transparent font-mono text-[14px] outline-none placeholder:text-ink-faint"
              />
            </IconInput>
          </div>
        </FieldRow>
      </div>

      <div className="-mt-1 flex flex-wrap items-center gap-1.5">
        {TIME_QUICK_PICKS.map((t) => {
          const active = timeValue === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() =>
                setValue("timeOnly", t, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              className={cn(
                "rounded-[7px] border px-2.5 py-1 font-mono text-[12px] font-semibold transition-colors",
                active
                  ? "border-foreground bg-foreground text-background"
                  : "border-border bg-transparent text-ink-soft hover:bg-muted",
              )}
            >
              {t}
            </button>
          );
        })}
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor="appt-notes" className="text-[11px] font-semibold text-ink-soft">
            Ghi chú
          </Label>
          <span
            aria-hidden="true"
            className="flex items-center gap-1 text-[11px] font-medium text-primary"
          >
            <Sparkles className="size-3" /> AI gợi ý
          </span>
        </div>
        <Textarea
          id="appt-notes"
          rows={3}
          maxLength={2000}
          placeholder="Thêm ghi chú về buổi xem nhà..."
          className="rounded-[9px] text-[12px] leading-relaxed"
          {...register("notes")}
        />
        {errors.notes ? (
          <p role="alert" className="text-sm text-destructive">
            {errors.notes.message}
          </p>
        ) : null}
      </div>

      <div className="mt-1 flex flex-col-reverse gap-2 border-t border-border pt-3 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Hủy
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <Loader2 aria-hidden="true" className="size-4 animate-spin" />
          ) : null}
          {submitLabel ?? (mode === "create" ? "Yêu cầu xem nhà" : "Lưu")}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

interface FieldRowProps {
  label: string;
  htmlFor: string | undefined;
  error?: string | undefined;
  children: React.ReactNode;
}

/** Compact field row: small ink-soft label above the control. */
function FieldRow({ label, htmlFor, error, children }: FieldRowProps) {
  return (
    <div className="space-y-1.5">
      {htmlFor !== undefined ? (
        <Label htmlFor={htmlFor} className="text-[11px] font-semibold text-ink-soft">
          {label}
        </Label>
      ) : (
        <p className="text-[11px] font-semibold text-ink-soft">{label}</p>
      )}
      {children}
      {error !== undefined ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Inline-icon wrapper styled like the design's "icon prefix + value" row. */
function IconInput({
  icon,
  className,
  children,
}: {
  icon: React.ReactNode;
  className?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex h-9 items-center gap-2 rounded-[9px] border border-border bg-background px-2.5 py-2 transition-colors focus-within:border-primary focus-within:ring-[3px] focus-within:ring-ring",
        className,
      )}
    >
      <span className="shrink-0 text-muted-foreground">{icon}</span>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Pads a number to two digits, e.g. 5 → "05". */
function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** Split an ISO datetime string into `{ dateOnly, timeOnly }` (local TZ). */
function splitDateTime(iso: string): { dateOnly: string; timeOnly: string } {
  if (!iso) return { dateOnly: "", timeOnly: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { dateOnly: "", timeOnly: "" };
  return {
    dateOnly: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    timeOnly: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

/** Combine a date (YYYY-MM-DD) + time (HH:mm) into a local ISO string. */
function combineDateTime(dateOnly: string, timeOnly: string): string | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeOnly);
  if (!dateMatch || !timeMatch) return null;
  const [, y, m, d] = dateMatch;
  const [, hh, mm] = timeMatch;
  const date = new Date(
    Number.parseInt(y!, 10),
    Number.parseInt(m!, 10) - 1,
    Number.parseInt(d!, 10),
    Number.parseInt(hh!, 10),
    Number.parseInt(mm!, 10),
    0,
    0,
  );
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

/** Default to one hour from now, rounded to the next 30-minute boundary. */
function defaultScheduledAt(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 60);
  d.setSeconds(0, 0);
  const minute = d.getMinutes();
  d.setMinutes(minute < 30 ? 30 : 0);
  if (minute >= 30) d.setHours(d.getHours() + 1);
  return d.toISOString();
}
